/**
 * The annual planner (årshjul) — the mosque's internal calendar.  MKK adoption Batch 1 (MKK_ADOPTION_BATCH_1.md §1.3).
 *
 * "What is happening this year, who owns it, and does it clash?" A planner item is a meeting, an event, a preparation,
 * a deadline or something else, on a mosque-local DAY, optionally with a time, optionally recurring. Internal: staff
 * only (planner.read / planner.write), behind the `planner` capability, not entitled for any mosque. A public event is
 * still an `event` (events.js); a planner item may point at one.
 *
 * Recurrence is ONE rule per item, computed when read — never written out as rows. So a series never silently stops at
 * a horizon, editing it never orphans copies, and a monthly date never drifts: every occurrence is computed from the
 * START date, not from the previous occurrence (31 Jan → 28/29 Feb → 31 Mar).
 *
 *   { freq: 'weekly',  interval: 1–12, until }                                   every N weeks, the start's weekday
 *   { freq: 'monthly', interval: 1–12, by: 'day'|'weekday'|'last_weekday', until }
 *        day           the start's day of the month, clamped to a shorter month's last day
 *        weekday       the start's Nth weekday (N = 1–4: "2nd Saturday")
 *        last_weekday  the last such weekday of the month
 *   { freq: 'yearly',  interval: 1–5, until }                                    the same month and day; 29 Feb → 28 Feb
 *
 * `until` is required and at most five years after the start: a series always has an end, and staff can see it.
 * A single occurrence can be cancelled (and restored); per-occurrence edits are not in Batch 1.
 *
 * HIJRI ANCHOR (work hub, 2026-10-31): a yearly item may carry `hijriMonth` 1–12 and `hijriDay` 1–30. Then its yearly
 * occurrences are that Hijri day of each Hijri year (hijri.js, with the mosque's moon-sighting offset passed in by the
 * caller as `offsetFor(date)`), not the same Gregorian month and day — Eid, Ramadan, Ashura and Mawlid are planned once
 * and fall right every year. `startsOn` is the Gregorian day the series begins (the first occurrence is the anchor in
 * that Hijri year, or the next one when the anchor is already past).
 *
 * SEEDS: a short catalogue of the fixed posts a Norwegian mosque's year has («Legg til faste poster»), each turned into
 * an itemInput() body from a date — nothing is seeded by itself.
 *
 * Pure: dates are 'YYYY-MM-DD' text (day.js), no clock, no time zone.
 */
const Day = require('./day');
const T = require('./tasks');
const H = require('./hijri');

const CATEGORIES = ['meeting', 'event', 'preparation', 'deadline', 'other'];
const CATEGORY_NO = { meeting: 'Møte', event: 'Arrangement', preparation: 'Forberedelse', deadline: 'Frist', other: 'Annet' };
const STATUSES = ['active', 'cancelled'];
const FREQS = { weekly: 12, monthly: 12, yearly: 5 };
const MONTHLY_BY = ['day', 'weekday', 'last_weekday'];
const MAX_TITLE = 160;
const MAX_DESCRIPTION = 2000;
const MAX_LOCATION = 160;
const MAX_ITEMS = 1000;
const MAX_WINDOW_DAYS = 400;
const MAX_OCCURRENCES = 400;
const MAX_YEARS = 5;
const FIELDS = ['title', 'description', 'category', 'startsOn', 'startTime', 'endTime', 'location', 'ownerId', 'eventId', 'recurrence', 'status', 'hijriMonth', 'hijriDay'];
/** The Hijri months, as the panel and the app name them (hijri.js). */
const HIJRI_MONTHS_NO = H.MONTHS_NO;
const NO_OFFSET = () => 0;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The Nth (1-based) occurrence of a weekday in its month, from the date: day 1–7 is 1st, 8–14 is 2nd, … */
const nthOf = (s) => Math.ceil(Day.parse(s).d / 7);
/** Whether `s` is the last such weekday of its month. */
const isLastWeekday = (s) => { const p = Day.parse(s); return p.d + 7 > Day.daysInMonth(p.y, p.m); };
/** The date of the Nth `wd` (0 = Sunday) in month m of y; n = -1 for the last. */
function weekdayInMonth(y, m, wd, n) {
  if (n === -1) {
    const last = Day.fmt(y, m, Day.daysInMonth(y, m));
    return Day.add(last, -((Day.weekday(last) - wd + 7) % 7));
  }
  const first = Day.fmt(y, m, 1);
  return Day.add(first, ((wd - Day.weekday(first) + 7) % 7) + (n - 1) * 7);
}
/** The same day five years on (29 Feb → 28 Feb). */
function yearsLater(s, years) { const p = Day.parse(s); return Day.fmt(p.y + years, p.m, Math.min(p.d, Day.daysInMonth(p.y + years, p.m))); }

/** A recurrence rule, checked against the item's start date. → { ok, value } | { ok: false, message } */
function recurrence(input, startsOn) {
  if (input === null || input === undefined) return { ok: true, value: null };
  if (typeof input !== 'object' || Array.isArray(input)) return { ok: false, message: 'Ugyldig gjentakelse.' };
  const unknown = Object.keys(input).filter((k) => !['freq', 'interval', 'by', 'until'].includes(k));
  if (unknown.length) return { ok: false, message: 'Ugyldig gjentakelse.' };
  const max = FREQS[input.freq];
  if (!max) return { ok: false, message: 'Velg ukentlig, månedlig eller årlig.' };
  const interval = input.interval === undefined ? 1 : input.interval;
  if (!Number.isInteger(interval) || interval < 1 || interval > max) return { ok: false, message: `Intervallet må være fra 1 til ${max}.` };
  if (!Day.valid(input.until)) return { ok: false, message: 'Velg når gjentakelsen slutter.' };
  if (startsOn && input.until < startsOn) return { ok: false, message: 'Sluttdatoen kan ikke være før startdatoen.' };
  if (startsOn && input.until > yearsLater(startsOn, MAX_YEARS)) return { ok: false, message: `En gjentakelse kan vare i maks ${MAX_YEARS} år. Forleng den senere.` };
  const out = { freq: input.freq, interval, until: input.until };
  if (input.freq === 'monthly') {
    const by = input.by === undefined ? 'day' : input.by;
    if (!MONTHLY_BY.includes(by)) return { ok: false, message: 'Velg samme dato, samme ukedag eller siste ukedag i måneden.' };
    if (startsOn && by === 'weekday' && nthOf(startsOn) > 4) return { ok: false, message: 'Startdatoen er den femte ukedagen i måneden. Velg «siste ukedag» i stedet.' };
    if (startsOn && by === 'last_weekday' && !isLastWeekday(startsOn)) return { ok: false, message: 'Startdatoen er ikke den siste slike ukedagen i måneden.' };
    out.by = by;
  } else if (input.by !== undefined) return { ok: false, message: 'Ugyldig gjentakelse.' };
  return { ok: true, value: out };
}

/**
 * A planner item as staff send it; `base` for a change. Unknown fields are refused.
 * → { ok, value, changed } | { ok: false, errors }
 */
function itemInput(input, base = null) {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? input : null;
  if (!src) return { ok: false, errors: { item: 'Ugyldig punkt.' } };
  const unknown = Object.keys(src).filter((k) => !FIELDS.includes(k) && k !== 'version');
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, 'Ukjent felt.'])) };
  const cur = base || { description: null, category: 'other', startTime: null, endTime: null, location: null, ownerId: null, eventId: null, recurrence: null, status: 'active',
    hijriMonth: null, hijriDay: null };
  const pick = (k) => (Object.prototype.hasOwnProperty.call(src, k) ? src[k] : cur[k]);
  const errors = {}, out = {};
  const take = (k, r) => { if (r.ok) out[k] = r.value; else errors[k] = r.message; };
  take('title', T.oneLine(pick('title'), 'en tittel', MAX_TITLE));
  take('description', T.text(pick('description'), 'beskrivelsen', MAX_DESCRIPTION));
  take('location', T.oneLine(pick('location'), 'sted', MAX_LOCATION, false));
  const category = pick('category');
  if (!CATEGORIES.includes(category)) errors.category = 'Velg en kategori.'; else out.category = category;
  const status = pick('status');
  if (!STATUSES.includes(status)) errors.status = 'Ugyldig status.'; else out.status = status;
  if (!base && status !== 'active') errors.status = 'Et nytt punkt er aktivt.';
  const startsOn = pick('startsOn');
  if (!Day.valid(startsOn)) errors.startsOn = 'Velg en dato (ÅÅÅÅ-MM-DD).'; else out.startsOn = startsOn;
  const st = pick('startTime'), et = pick('endTime');
  const none = (v) => v === null || v === undefined || v === '';
  if (none(st) && none(et)) { out.startTime = null; out.endTime = null; }
  else if (none(st) || !TIME_RE.test(st)) errors.startTime = 'Skriv starttid som TT:MM, eller la begge tidene stå tomme (hele dagen).';
  else if (!none(et) && !TIME_RE.test(et)) errors.endTime = 'Skriv sluttid som TT:MM.';
  else if (!none(et) && et <= st) errors.endTime = 'Sluttid må være etter starttid.';
  else { out.startTime = st; out.endTime = none(et) ? null : et; }
  for (const k of ['ownerId', 'eventId']) {
    const v = pick(k);
    if (none(v)) out[k] = null; else if (T.isId(v)) out[k] = v; else errors[k] = 'Ukjent valg.';
  }
  const r = recurrence(pick('recurrence'), out.startsOn);
  take('recurrence', r);
  // The Hijri anchor: both numbers or neither, in range, and only on a yearly series (a weekly one has no Hijri day).
  const hm = pick('hijriMonth'), hd = pick('hijriDay');
  if (none(hm) && none(hd)) { out.hijriMonth = null; out.hijriDay = null; }
  else if (!Number.isInteger(hm) || hm < 1 || hm > 12) errors.hijriMonth = 'Velg en hijri-måned (1–12).';
  else if (!Number.isInteger(hd) || hd < 1 || hd > 30) errors.hijriDay = 'Velg en dag i hijri-måneden (1–30).';
  else if (!r.ok || !r.value || r.value.freq !== 'yearly') errors.hijriMonth = 'En hijri-dato gjelder bare for et punkt som gjentas årlig.';
  else { out.hijriMonth = hm; out.hijriDay = hd; }
  if (Object.keys(errors).length) return { ok: false, errors };
  const same = (k) => JSON.stringify(base[k] ?? null) === JSON.stringify(out[k] ?? null);
  const changed = base ? FIELDS.filter((k) => !same(k)) : FIELDS.filter((k) => out[k] != null && k !== 'status');
  return { ok: true, value: out, changed };
}

/** Whether an item's yearly series follows a Hijri day. */
const hijriAnchored = (item) => Boolean(item && item.recurrence && item.recurrence.freq === 'yearly' && item.hijriMonth && item.hijriDay);
/** The Hijri year of a Gregorian day, with the mosque's offset on that day. */
function hijriYearOf(date, offsetFor) {
  const p = Day.parse(date);
  return H.toHijri(new Date(p.y, p.m - 1, p.d), offsetFor(date)).year;
}
/**
 * The Gregorian day of a Hijri date in the mosque's calendar: the offset in force on the day it lands on (found with no
 * offset first, then read again with that day's own). Null for a Hijri date no tabular year has (a 30th of a 29-day month).
 */
function hijriDate(year, month, day, offsetFor) {
  // A 30th in a 29-day month (months 2, 4, 6, 8, 10, and 12 in a common year) is that month's last day, the 29th — never no day,
  // which would end the series silently (review P2, 2026-10-31).
  const d = day === 30 && !H.fromHijri(year, month, 30, 0) ? 29 : day;
  const rough = H.fromHijri(year, month, d, 0);
  if (!rough) return null;
  return H.fromHijri(year, month, d, offsetFor(rough));
}

/** The k-th date of a series (k = 0 is the start), or null past `until`. `offsetFor(date)`: the moon-sighting offset. */
function nth(item, k, offsetFor = NO_OFFSET) {
  const r = item.recurrence;
  if (!r) return k === 0 ? item.startsOn : null;
  // A stored rule whose end is not a real day (written around the API) has no dates at all — never an exception or a
  // series without end. The API names it in invalidDates, so it can be fixed.
  if (!Day.valid(r.until)) return null;
  const p = Day.parse(item.startsOn);
  let d;
  if (hijriAnchored(item)) {
    // The anchor in the Hijri year the series starts in — or the next, when it is already past on the start day.
    let y0 = hijriYearOf(item.startsOn, offsetFor);
    const first = hijriDate(y0, item.hijriMonth, item.hijriDay, offsetFor);
    if (first !== null && first < item.startsOn) y0 += 1;
    d = hijriDate(y0 + k * r.interval, item.hijriMonth, item.hijriDay, offsetFor);
    if (d === null) return null;
  } else if (r.freq === 'weekly') d = Day.add(item.startsOn, k * 7 * r.interval);
  else if (r.freq === 'monthly') {
    const { y, m } = Day.addMonths(p.y, p.m, k * r.interval);
    if (r.by === 'weekday') d = weekdayInMonth(y, m, Day.weekday(item.startsOn), nthOf(item.startsOn));
    else if (r.by === 'last_weekday') d = weekdayInMonth(y, m, Day.weekday(item.startsOn), -1);
    else d = Day.fmt(y, m, Math.min(p.d, Day.daysInMonth(y, m)));
  } else d = yearsLater(item.startsOn, k * r.interval);
  return d <= r.until ? d : null;
}

/**
 * The occurrences of an item from `from` to `to` (inclusive), in order: [{ date, cancelled }]. A cancelled ITEM has
 * none. At most MAX_OCCURRENCES. `cancelledDates` are the single dates cancelled (planner_occurrence).
 */
function occurrences(item, from, to, cancelledDates = [], { offsetFor = NO_OFFSET } = {}) {
  // An item whose stored start is not a real day has no occurrences (Batch 1 remediation) — never an exception.
  if (!Day.valid(from) || !Day.valid(to) || to < from || item.status === 'cancelled' || !Day.valid(item.startsOn)) return [];
  const cancelled = new Set(cancelledDates);
  const r = item.recurrence;
  // Jump close to the window rather than walking from the start: k from a safe estimate below the first occurrence.
  let k = 0;
  if (r && from > item.startsOn) {
    const days = Day.diff(item.startsOn, from);
    const per = r.freq === 'weekly' ? 7 * r.interval : r.freq === 'monthly' ? 31 * r.interval : (hijriAnchored(item) ? 355 : 366) * r.interval;
    k = Math.max(0, Math.floor(days / per) - 1);
  }
  const out = [];
  for (; out.length < MAX_OCCURRENCES; k++) {
    const d = nth(item, k, offsetFor);
    if (d === null || d > to) break;
    if (d >= from) out.push({ date: d, cancelled: cancelled.has(d) });
  }
  return out;
}

/** Whether `date` is one of the item's occurrences (cancelled or not). */
function isOccurrence(item, date, opts) {
  return Day.valid(date) && occurrences({ ...item, status: 'active' }, date, date, [], opts).length === 1;
}

/** After a series is edited: the cancelled dates that are still occurrences (kept) and how many were dropped. */
function keepCancellations(item, cancelledDates, opts) {
  const kept = cancelledDates.filter((d) => isOccurrence(item, d, opts));
  return { kept, dropped: cancelledDates.length - kept.length };
}

/** A query window: both dates valid, from ≤ to, at most MAX_WINDOW_DAYS. */
function window(from, to) {
  if (!Day.valid(from) || !Day.valid(to)) return { ok: false, message: 'Velg en periode (ÅÅÅÅ-MM-DD).' };
  if (to < from) return { ok: false, message: 'Perioden slutter før den begynner.' };
  if (Day.diff(from, to) > MAX_WINDOW_DAYS) return { ok: false, message: `En periode kan være maks ${MAX_WINDOW_DAYS} dager.` };
  return { ok: true, value: { from, to } };
}

/** How a rule reads in the panel: "gjentas hver 2. uke til 31.12.2027"; a Hijri anchor: "hvert år 10. Dhu al-Hijjah (hijri) til …". */
function describe(r, item = null) {
  if (!r || !Day.valid(r.until)) return null;
  const every = r.interval === 1 ? '' : `${r.interval}. `;
  const hijri = item && item.hijriMonth && item.hijriDay && r.freq === 'yearly';
  const what = r.freq === 'weekly' ? `hver ${every}uke`
    : r.freq === 'yearly' ? (hijri ? `hvert ${every}år ${item.hijriDay}. ${HIJRI_MONTHS_NO[item.hijriMonth - 1]} (hijri)` : `hvert ${every}år`)
    : r.by === 'weekday' ? `hver ${every}måned, samme ukedag` : r.by === 'last_weekday' ? `hver ${every}måned, siste ukedag` : `hver ${every}måned, samme dato`;
  const u = Day.parse(r.until);
  return `gjentas ${what} til ${u.d}.${u.m}.${u.y}`;
}

/* ---------------------------------------------------------------- seeds ---------------------------------------------------------------- */

/**
 * «Legg til faste poster» (owner 2026-10-31): the posts most years have, as a catalogue. `when` says where the first
 * occurrence lands, counted from `today`:
 *   { hijri: { month, day } }                  a Hijri day (the series follows the Hijri calendar; yearly);
 *   { month, day }                             a Gregorian day (yearly);
 *   { weekday, every: 'week' | 'month' }       the next such weekday (weekly), or the next Nth-weekday of a month (monthly, same weekday);
 *   { weekday, every: 'last' }                 the last such weekday of the next month (monthly, last weekday).
 * Weekday 0 = Sunday, as day.js counts. Nothing here is written anywhere by itself.
 */
const SEEDS = [
  { key: 'arsmote', title: 'Årsmøte', category: 'meeting', when: { month: 3, day: 15 }, startTime: '18:00', endTime: '20:00' },
  { key: 'ramadan_prep', title: 'Ramadan-forberedelser', category: 'preparation', when: { hijri: { month: 8, day: 15 } } },
  { key: 'eid_fitr', title: 'Eid al-Fitr', category: 'event', when: { hijri: { month: 10, day: 1 } } },
  { key: 'eid_adha', title: 'Eid al-Adha', category: 'event', when: { hijri: { month: 12, day: 10 } } },
  { key: 'nikah', title: 'Nikah-kveld', category: 'event', when: { weekday: 6, every: 'last' }, startTime: '18:00', endTime: '21:00' },
  { key: 'styremote_weekly', title: 'Styremøte (ukentlig)', category: 'meeting', when: { weekday: 2, every: 'week' }, startTime: '18:00', endTime: '20:00' },
  { key: 'styremote_monthly', title: 'Styremøte (månedlig)', category: 'meeting', when: { weekday: 2, every: 'month' }, startTime: '18:00', endTime: '20:00' },
  { key: 'dugnad', title: 'Dugnad', category: 'other', when: { month: 5, day: 1 }, startTime: '10:00', endTime: '14:00' },
];
const SEED_YEARS = 3;

/** The first day on or after `today` that is weekday `wd`. */
const nextWeekday = (today, wd) => Day.add(today, (wd - Day.weekday(today) + 7) % 7);

/**
 * A seed → the body itemInput() takes, with the first occurrence on or after `today` and a series of SEED_YEARS years.
 * `offsetFor(date)`: the mosque's moon-sighting offset. Null for a seed that cannot land (a Hijri day that does not exist).
 */
function seedInput(seed, today, { offsetFor = NO_OFFSET } = {}) {
  if (!seed || !Day.valid(today)) return null;
  const w = seed.when;
  let startsOn, recurrence, hijri = null;
  if (w.hijri) {
    let y = hijriYearOf(today, offsetFor);
    startsOn = hijriDate(y, w.hijri.month, w.hijri.day, offsetFor);
    if (startsOn !== null && startsOn < today) startsOn = hijriDate(y + 1, w.hijri.month, w.hijri.day, offsetFor);
    if (startsOn === null) return null;
    recurrence = { freq: 'yearly', interval: 1 };
    hijri = w.hijri;
  } else if (w.month) {
    const p = Day.parse(today);
    startsOn = Day.fmt(p.y, w.month, w.day);
    if (startsOn < today) startsOn = Day.fmt(p.y + 1, w.month, w.day);
    recurrence = { freq: 'yearly', interval: 1 };
  } else if (w.every === 'week') {
    startsOn = nextWeekday(today, w.weekday);
    recurrence = { freq: 'weekly', interval: 1 };
  } else if (w.every === 'month') {
    const p = Day.parse(today);
    startsOn = weekdayInMonth(p.y, p.m, w.weekday, 1);
    if (startsOn < today) { const n = Day.addMonths(p.y, p.m, 1); startsOn = weekdayInMonth(n.y, n.m, w.weekday, 1); }
    recurrence = { freq: 'monthly', interval: 1, by: 'weekday' };
  } else {
    const p = Day.parse(today);
    startsOn = weekdayInMonth(p.y, p.m, w.weekday, -1);
    if (startsOn < today) { const n = Day.addMonths(p.y, p.m, 1); startsOn = weekdayInMonth(n.y, n.m, w.weekday, -1); }
    recurrence = { freq: 'monthly', interval: 1, by: 'last_weekday' };
  }
  recurrence.until = yearsLater(startsOn, SEED_YEARS);
  return { title: seed.title, category: seed.category, startsOn, startTime: seed.startTime || null, endTime: seed.endTime || null,
    recurrence, hijriMonth: hijri ? hijri.month : null, hijriDay: hijri ? hijri.day : null };
}

module.exports = { CATEGORIES, CATEGORY_NO, STATUSES, FIELDS, MAX_TITLE, MAX_DESCRIPTION, MAX_LOCATION, MAX_ITEMS, MAX_WINDOW_DAYS, MAX_OCCURRENCES, MAX_YEARS,
  HIJRI_MONTHS_NO, SEEDS, SEED_YEARS,
  recurrence, itemInput, nth, occurrences, isOccurrence, keepCancellations, window, describe, weekdayInMonth, hijriAnchored, hijriDate, seedInput };
